import { startTransition, useOptimistic } from 'react';
import { useSearchParams } from 'next/navigation';

import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { GenericTrackingEvent } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { UseDisplayModeFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseDisplayModeFragmentFragmentDoc';

import { type DesktopDisplayMode } from '../types';
import { encodeDisplayMode } from '../queryEncoding';
import { getDeviceIndependentDisplayMode } from '../utils/getDeviceIndependentDisplayMode';

const gtmMapping = {
  cards: 'galerie_4_spaltig',
  largeCards: 'galerie_3_spaltig',
  moreInformation: 'liste',
};

export const useDisplayMode = (
  maskedData: FragmentType<typeof UseDisplayModeFragmentFragmentDoc>,
) => {
  const dispatchGTMEvent = useTracking();
  const { isDesktop } = useDeviceType();
  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const { displayMode, defaultDisplayMode } = getDeviceIndependentDisplayMode(
    maskedData,
    searchParams,
  );

  const [optimisticDisplayMode, setOptimisticDisplayMode] = useOptimistic(displayMode);
  const mobileDisplayMode = displayMode === 'largeCards' ? 'cards' : displayMode;

  const pushDisplayModeChange = (newDisplayMode: DesktopDisplayMode) => {
    startTransition(() => setOptimisticDisplayMode(newDisplayMode));
    // default display mode should not be visible in the URL
    const cleanDisplayMode = newDisplayMode === defaultDisplayMode ? undefined : newDisplayMode;

    pushQueryChange(encodeDisplayMode(cleanDisplayMode));

    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `view=${gtmMapping[newDisplayMode]}`,
    });
  };

  return {
    displayMode: isDesktop ? optimisticDisplayMode : mobileDisplayMode,
    pushDisplayModeChange,
  };
};
